import {
  Box,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import { Delete } from "@mui/icons-material";
import axios from "axios";
import { useAuth } from "../../../auth/useAuth";
import { can } from "../../../auth/can";
import { useState } from "react";

import { APP_API_V1_BASE } from "../../../config/api";
const API_BASE = `${APP_API_V1_BASE}/organizations`;

export default function ActiveUsersTable({ rows, setRows, orgId }) {
  const { activeOrg, permissions, user } = useAuth();
  const currentUserId = user?.id;
  const token = localStorage.getItem("authToken");

  const headers = {
    Authorization: `Bearer ${token}`,
    ...(activeOrg?.id ? { "X-Org-ID": activeOrg.id } : {}),
  };

  // -----------------------
  // Helpers
  // -----------------------
  const isSelf = (rowUserId) => rowUserId === currentUserId;

  const isLastOrgAdmin = (rowUserId) => {
    const admins = rows.filter((u) => u.role === "org_admin");
    return admins.length === 1 && admins[0].user_id === rowUserId;
  };

  const isGlobalSuperAdmin = (row) => row.is_global_superadmin === true;

  const formatActivityTime = (value) => {
    if (!value) return "Never";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Never";

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // -----------------------
  // API actions
  // -----------------------
  const updateRole = async (userId, role) => {
    await axios.patch(
      `${API_BASE}/${orgId}/users/${userId}/role`,
      { role },
      { headers },
    );

    setRows((prev) =>
      prev.map((u) => (u.user_id === userId ? { ...u, role } : u)),
    );
  };

  const updateStatus = async (userId, status) => {
    await axios.patch(
      `${API_BASE}/${orgId}/users/${userId}/status`,
      { status },
      { headers },
    );

    setRows((prev) =>
      prev.map((u) => (u.user_id === userId ? { ...u, status } : u)),
    );
  };

  const removeUser = async () => {
    if (!selectedUserId) return;

    await axios.delete(`${API_BASE}/${orgId}/users/${selectedUserId}`, {
      headers,
    });

    setRows((prev) => prev.filter((u) => u.user_id !== selectedUserId));

    setOpenDialog(false);
    setSelectedUserId(null);
  };
  // -----------------------
  // Columns
  // -----------------------

  const baseColumns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      renderCell: (params) => {
        const self = isSelf(params.row.user_id);
        const superAdmin = isGlobalSuperAdmin(params.row);

        return (
          <Box display="flex" alignItems="center" gap={1}>
            {params.value || params.row.email}

            {self && <Chip label="You" size="small" color="primary" />}

            {superAdmin && (
              <Chip label="Super Admin" size="small" color="secondary" />
            )}
          </Box>
        );
      },
    },
    { field: "email", headerName: "Email", flex: 1 },
  ];

  const columns = [...baseColumns];

  if (can(permissions, "organization.user.update_role")) {
    columns.push({
      field: "role",
      headerName: "Role",
      width: 180,
      renderCell: (params) => {
        const self = isSelf(params.row.user_id);
        const lastAdmin = isLastOrgAdmin(params.row.user_id);
        const superAdmin = isGlobalSuperAdmin(params.row);

        const disabled =
          self ||
          lastAdmin ||
          !can(permissions, "organization.user.update_role");

        let tooltip = "";
        if (superAdmin)
          tooltip = "Global superadmin — organization role is informational";
        else if (self) tooltip = "You cannot change your own role";
        else if (lastAdmin)
          tooltip = "At least one organization admin is required";

        return (
          <Tooltip title={disabled ? tooltip : ""}>
            <span>
              <Select
                size="small"
                value={params.value}
                disabled={disabled}
                onChange={(e) => updateRole(params.row.user_id, e.target.value)}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="org_admin">Org Admin</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="operator">Operator</MenuItem>
                <MenuItem value="viewer">Viewer</MenuItem>
              </Select>
            </span>
          </Tooltip>
        );
      },
    });
  }

  if (can(permissions, "organization.user.update_status")) {
    columns.push({
      field: "status",
      headerName: "Status",
      width: 140,
      renderCell: (params) => {
        const self = isSelf(params.row.user_id);
        const lastAdmin = isLastOrgAdmin(params.row.user_id);
        const superAdmin = isGlobalSuperAdmin(params.row);

        const disabled =
          self ||
          lastAdmin ||
          superAdmin ||
          !can(permissions, "organization.user.update_status");

        let tooltip = "";
        if (superAdmin) tooltip = "Global superadmin status cannot be changed";
        else if (self) tooltip = "You cannot change your own status";
        else if (lastAdmin)
          tooltip = "At least one organization admin must stay active";

        return (
          <Tooltip title={disabled ? tooltip : ""}>
            <span>
              <Switch
                checked={params.value === "active"}
                disabled={disabled}
                onChange={() =>
                  updateStatus(
                    params.row.user_id,
                    params.value === "active" ? "suspended" : "active",
                  )
                }
              />
            </span>
          </Tooltip>
        );
      },
    });
  }

  if (can(permissions, "organization.user.remove")) {
    columns.push({
      field: "actions",
      headerName: "",
      width: 80,
      renderCell: (params) => {
        const self = isSelf(params.row.user_id);
        const lastAdmin = isLastOrgAdmin(params.row.user_id);
        const superAdmin = isGlobalSuperAdmin(params.row);

        const disabled =
          self ||
          lastAdmin ||
          superAdmin ||
          !can(permissions, "organization.user.remove");

        let tooltip = "";
        if (superAdmin) tooltip = "Global superadmin cannot be removed";
        else if (self) tooltip = "You cannot remove yourself";
        else if (lastAdmin)
          tooltip = "At least one organization admin is required";

        return (
          <Tooltip title={disabled ? tooltip : "Remove user"}>
            <span>
              <IconButton
                color="error"
                disabled={disabled}
                onClick={() => {
                  setSelectedUserId(params.row.user_id);
                  setOpenDialog(true);
                }}
              >
                <Delete />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    });
  }
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const paginatedRows = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );
  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        }}
      >
        <Table>
          {/* ✅ HEADER */}
          <TableHead>
            <TableRow
              sx={{
                background:
                  "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
              }}
            >
              <TableCell sx={{ color: "#E2E8F0", fontWeight: 700 }}>
                Name
              </TableCell>
              <TableCell sx={{ color: "#E2E8F0", fontWeight: 700 }}>
                Email
              </TableCell>
              <TableCell sx={{ color: "#E2E8F0", fontWeight: 700 }}>
                Last Login
              </TableCell>
              <TableCell sx={{ color: "#E2E8F0", fontWeight: 700 }}>
                Last Logout
              </TableCell>
              {can(permissions, "organization.user.update_role") && (
                <TableCell sx={{ color: "#E2E8F0", fontWeight: 700 }}>
                  Role
                </TableCell>
              )}
              {can(permissions, "organization.user.update_status") && (
                <TableCell sx={{ color: "#E2E8F0", fontWeight: 700 }}>
                  Status
                </TableCell>
              )}
              {can(permissions, "organization.user.remove") && (
                <TableCell sx={{ color: "#E2E8F0", fontWeight: 700 }}>
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          {/* ✅ BODY */}
          <TableBody>
            {paginatedRows.map((row) => {
              const self = isSelf(row.user_id);
              const lastAdmin = isLastOrgAdmin(row.user_id);
              const superAdmin = isGlobalSuperAdmin(row);

              return (
                <TableRow
                  key={row.user_id}
                  sx={{
                    backgroundColor:
                      row.user_id === currentUserId ? "#E0F2FE" : "#F8FAFC",
                    "&:hover": {
                      backgroundColor: "#E2E8F0",
                    },
                  }}
                >
                  {/* ✅ NAME */}
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {row.name || row.email}

                      {self && (
                        <Chip label="You" size="small" color="primary" />
                      )}
                      {superAdmin && (
                        <Chip
                          label="Super Admin"
                          size="small"
                          color="secondary"
                        />
                      )}
                    </Box>
                  </TableCell>

                  {/* ✅ EMAIL */}
                  <TableCell>{row.email}</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    {formatActivityTime(row.last_login_at)}
                  </TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    {formatActivityTime(row.last_logout_at)}
                  </TableCell>

                  {/* ✅ ROLE */}
                  {can(permissions, "organization.user.update_role") && (
                    <TableCell>
                      <Tooltip
                        title={
                          superAdmin
                            ? "Global superadmin — role is informational"
                            : self
                              ? "You cannot change your own role"
                              : lastAdmin
                                ? "At least one admin required"
                                : ""
                        }
                      >
                        <span>
                          <Select
                            size="small"
                            value={row.role}
                            disabled={self || lastAdmin || superAdmin}
                            onChange={(e) =>
                              updateRole(row.user_id, e.target.value)
                            }
                            sx={{ minWidth: 140 }}
                          >
                            <MenuItem value="org_admin">Org Admin</MenuItem>
                            <MenuItem value="manager">Manager</MenuItem>
                            <MenuItem value="operator">Operator</MenuItem>
                            <MenuItem value="viewer">Viewer</MenuItem>
                          </Select>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}

                  {/* ✅ STATUS */}
                  {can(permissions, "organization.user.update_status") && (
                    <TableCell>
                      <Tooltip
                        title={
                          superAdmin
                            ? "Cannot change superadmin"
                            : self
                              ? "You cannot change your own status"
                              : lastAdmin
                                ? "At least one admin must stay active"
                                : ""
                        }
                      >
                        <span>
                          <Switch
                            checked={row.status === "active"}
                            disabled={self || lastAdmin || superAdmin}
                            onChange={() =>
                              updateStatus(
                                row.user_id,
                                row.status === "active"
                                  ? "suspended"
                                  : "active",
                              )
                            }
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}

                  {/* ✅ ACTIONS */}
                  {can(permissions, "organization.user.remove") && (
                    <TableCell>
                      <Tooltip
                        title={
                          superAdmin
                            ? "Cannot remove superadmin"
                            : self
                              ? "You cannot remove yourself"
                              : lastAdmin
                                ? "At least one admin required"
                                : "Remove user"
                        }
                      >
                        <span>
                          <IconButton
                            color="error"
                            disabled={self || lastAdmin || superAdmin}
                            onClick={() => {
                              setSelectedUserId(row.user_id);
                              setOpenDialog(true);
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
        sx={{
          borderTop: "1px solid #E2E8F0",
          backgroundColor: "#F8FAFC",

          "& .MuiTablePagination-toolbar": {
            px: 2,
          },

          "& .MuiTablePagination-select": {
            borderRadius: "8px",
          },
        }}
      />
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove this user from the organization?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={removeUser}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
